import {
	type ProjectPhasesAndTaskSetsSaveInput,
	useProjectPhasesAndTaskSetsSaveMutation,
	useProjectPhasesModalQuery,
	type Project,
} from '@/generated/graphql';
import { computed, ref } from 'vue';
import { useMessage } from '@/features/Message';
import { useTranslation } from '@/composables';
import {
	parseProjectPhases,
	transformPhasesToInput,
} from '@/features/ProjectPhase/components/ProjectPhases/utils';
import cloneDeep from 'lodash/cloneDeep';
import { type PhaseAndMilestoneListItemType } from '@/features/ProjectPhase';

export const useProjectPhasesModal = (projectId: Project['id']) => {
	const { t } = useTranslation();
	const { addSuccessMessage, addErrorMessage } = useMessage();

	const projectPhases = ref<PhaseAndMilestoneListItemType[]>([]);
	const hasBackToBackPhases = ref(false);

	const {
		result: queryResult,
		loading: initQueryLoading,
		onError: onQueryError,
		onResult,
	} = useProjectPhasesModalQuery(
		() => ({
			projectParams: {
				projectId: Number(projectId),
			},
		}),
		() => ({
			fetchPolicy: 'no-cache',
			enabled: !!projectId,
		})
	);

	onQueryError(() => {
		addErrorMessage({
			message: t('project_modify_save_failed_unique_general'),
		});
	});

	onResult((result) => {
		if (result.data) {
			if (result.data.PhasesTaskSets) {
				projectPhases.value = cloneDeep(
					parseProjectPhases(result.data.PhasesTaskSets)
				);
			}

			hasBackToBackPhases.value =
				result.data.Project?.hasBackToBackPhases ?? false;
		}
	});

	const projectStartDate = computed(
		() => queryResult.value?.Project?.date ?? ''
	);

	const numberOfTasks = computed(
		() => queryResult.value?.Project?.numberOfTasks ?? 0
	);

	const {
		mutate: projectPhasesAndTaskSetsSaveMutate,
		loading: isSavingInProgress,
		onError: onErrorSaving,
	} = useProjectPhasesAndTaskSetsSaveMutation({
		context: {
			handleErrorLocally: true,
		},
	});

	onErrorSaving((error) => {
		const errorMessage = error?.message ?? '';
		addErrorMessage({
			message: `${t('project_modify_save_failed_unique_general')}: ${errorMessage}`,
		});
	});

	const savePhases = async (): Promise<boolean> => {
		if (!projectId) {
			return false;
		}

		const canReOrderPhases = numberOfTasks.value === 0;
		const { phases, taskSets } = transformPhasesToInput(
			projectPhases.value,
			canReOrderPhases
		);

		const phasesInput: ProjectPhasesAndTaskSetsSaveInput = {
			projectId: projectId,
			hasBackToBackPhases: hasBackToBackPhases.value,
			phases,
			taskSets,
		};

		try {
			await projectPhasesAndTaskSetsSaveMutate({
				input: phasesInput,
			});

			addSuccessMessage({
				message: t('form_save_success'),
			});

			return true;
		} catch {
			addErrorMessage({
				message: t('project_modify_save_failed_unique_general'),
			});

			return false;
		}
	};

	return {
		projectPhases,
		hasBackToBackPhases,
		projectStartDate,
		numberOfTasks,
		initQueryLoading,
		isSavingInProgress,
		savePhases,
	};
};
